import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { COMPANY_LOGO } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sp.get("expired") === "1") {
      toast.info("Oturumunuz sona erdi. Lütfen tekrar giriş yapın.");
      // Reset the flag so refresh doesn't re-toast
      window.__madymark_401_redirect__ = false;
    }
  }, [sp]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Hoş geldiniz");
      nav("/");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Giriş başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-[#1E2A9C] text-white p-14 relative overflow-hidden">
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#29B7E3]/30 blur-3xl" />
        <div className="absolute -top-20 -left-20 w-[400px] h-[400px] rounded-full bg-[#29B7E3]/20 blur-3xl" />
        <div className="relative z-10 flex items-center gap-4">
          <img src={COMPANY_LOGO} alt="MADYMARK" className="w-14 h-14 object-contain bg-white rounded-xl p-1" />
          <div>
            <div className="font-display font-extrabold text-2xl">MADYMARK</div>
            <div className="text-xs uppercase tracking-widest text-cyan-200">İç & Dış Ticaret Sanayi A.Ş</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-display text-5xl font-extrabold leading-tight">
            Finansınızı<br />tek merkezden<br />yönetin.
          </h1>
          <p className="mt-6 text-cyan-100 max-w-md">
            Gelir, gider, banka hesapları, cari hesaplar ve proforma fatura yönetimi — hepsi tek bir ekranda.
          </p>
          <div className="mt-8 flex gap-8 text-xs uppercase tracking-widest text-cyan-200/80">
            <div><span className="text-white font-mono-num text-2xl block">3</span>Para Birimi</div>
            <div><span className="text-white font-mono-num text-2xl block">∞</span>Cari Hesap</div>
            <div><span className="text-white font-mono-num text-2xl block">PDF</span>Export</div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-cyan-200/70">
          © 2026 MADYMARK · Başakşehir V.D. · VKN 6101146549
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 via-cyan-50 to-blue-50">
        <form onSubmit={submit} className="w-full max-w-md bg-white p-10 rounded-2xl border border-[#29B7E3]/30 shadow-2xl shadow-[#1E2A9C]/20 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#1E2A9C] via-[#29B7E3] to-[#1E2A9C]" />
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={COMPANY_LOGO} alt="MADYMARK" className="w-20 h-20 object-contain" />
            <div className="font-display font-extrabold text-2xl">MADYMARK</div>
          </div>
          <div className="hidden lg:flex justify-center mb-6">
            <img src={COMPANY_LOGO} alt="MADYMARK" className="w-24 h-24 object-contain" />
          </div>
          <div className="text-[11px] uppercase tracking-widest text-[#29B7E3] font-bold text-center">Giriş</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 text-slate-900 text-center bg-gradient-to-r from-[#1E2A9C] to-[#29B7E3] bg-clip-text text-transparent">Çalışma Alanına Hoş Geldiniz</h2>
          <p className="text-slate-500 text-sm mt-2 mb-8 text-center">Şirket finans hesabınıza giriş yapın.</p>

          <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">E-posta</label>
          <input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mt-2 mb-5 px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#29B7E3]/40 focus:border-[#1E2A9C]"
            required
          />
          <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Şifre</label>
          <input
            data-testid="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mt-2 mb-8 px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#29B7E3]/40 focus:border-[#1E2A9C]"
            required
          />
          <button
            data-testid="login-submit"
            disabled={busy}
            className="w-full py-3 rounded-lg bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] transition disabled:opacity-60"
          >
            {busy ? "Giriş yapılıyor..." : "Giriş Yap"}
          </button>
          <div className="text-center mt-4">
            <Link to="/sifremi-unuttum" data-testid="link-forgot"
              className="text-xs text-[#1E2A9C] font-bold hover:underline">
              Şifremi Unuttum
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
